<template>
  <div class="blind-window" ref="blindw">
    <slot />
  </div>
</template>

<script lang="ts" setup>
interface Props {
  itemWidth?: number
  expansionRate?: number
  gap?: number
  initialPosition?: number
}
const props = withDefaults(defineProps<Props>(), {
  itemWidth: null,
  expansionRate: 3,
  gap: 6,
  initialPosition: 0,
})

const blindw = ref<HTMLElement>()

const blind = (index: number = 0, init: boolean = false) => {
  const items = blindw.value.querySelectorAll('.blind-window-item')

  const activeItem = items[index] as HTMLElement
  if (!activeItem) return
  if (activeItem.classList.contains('blind-enter')) {
    return
  }
  const containerWidth = blindw.value.clientWidth
  const itemWidth = (() => {
    if (props.itemWidth) {
      return props.itemWidth
    }
    return Math.max(
      48,
      (containerWidth - props.gap * (items.length - 1)) /
        (items.length + props.expansionRate - 1),
    )
  })()
  const expandWidth = itemWidth * props.expansionRate
  const diff = expandWidth - itemWidth

  items.forEach((item: HTMLElement) => {
    if (item.classList.contains('blind-enter')) {
      item.classList.add('blind-leave')
      item.classList.remove('blind-enter')
    }
  })

  activeItem.classList.add('blind-enter')
  activeItem.classList.add('blind-active')
  // 500msだけ.blind-activeを付与する。タッチデバイスのクリックイベント遅延300msに対応するため
  setTimeout(() => activeItem.classList.remove('blind-active'), 500)

  let before = true
  items.forEach((item: HTMLElement) => {
    const left = item.querySelector(
      '.blind-window-item-container-left',
    ) as HTMLElement
    const right = item.querySelector(
      '.blind-window-item-container-right',
    ) as HTMLElement
    const contents = item.querySelector(
      '.blind-window-item-container',
    ) as HTMLElement

    item.style.minWidth = `${itemWidth}px`
    left.style.minWidth = `${expandWidth}px`
    right.style.minWidth = `${expandWidth}px`

    if (init) {
      item.style.transitionDuration = '0s'
      contents.style.transitionDuration = '0s'
    } else {
      item.style.transitionDuration = ''
      contents.style.transitionDuration = ''
    }

    if (item.classList.contains('blind-enter')) {
      before = false
      item.style.transform = `translate3d(0, 0, 0)`
      left.style.minWidth = `${expandWidth}px`
      left.style.transform = `translate3d(0, 0, 0)`
      right.style.minWidth = `${expandWidth}px`
      right.style.transform = `translate3d(0, 0, 0)`
      contents.style.transform = `translate3d(0, 0, 0)`
    } else {
      left.style.transform = `translate3d(-${diff}px, 0, 0)`
      right.style.transform = `translate3d(${diff}px, 0, 0)`
      contents.style.transform = `translate3d(-${diff / 2}px, 0, 0)`
      if (before) {
        item.style.transform = `translate3d(0, 0, 0)`
      } else {
        item.style.transform = `translate3d(${diff}px, 0, 0)`
      }
    }
  })
}

onMounted(() => {
  blindw.value.style.gap = `${props.gap}px`
  const items = blindw.value.querySelectorAll('.blind-window-item')
  blind(props.initialPosition, true)

  items.forEach((item: HTMLElement, i: number) => {
    item.addEventListener('mouseover', () => {
      blind(i)
    })

    /**
     * タッチデバイスの時、一度目のクリック（mouseoverが先に発火する）はアイテム内のクリックイベントをキャンセルする。
     * 拡大された状態でクリックした時にアイテム内のイベント(aタグ等)が発火する。
     */
    item.addEventListener(
      'click',
      (e: MouseEvent) => {
        if (item.classList.contains('blind-active')) {
          e.stopPropagation()
          e.preventDefault()
        }
      },
      { capture: true },
    )

    item.addEventListener('transitionend', () => {
      item.classList.remove('blind-leave')
    })
  })
})
</script>

<style lang="scss" scoped>
.blind-window {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
}
</style>
